import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, api } from '../context/AppContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Leaf, DollarSign, Package, ExternalLink, Plus, Trash2, 
  Trophy, TrendingUp, ShoppingBag, Loader2, Car
} from 'lucide-react';
import { toast } from 'sonner';

const BudgetHero = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [itemName, setItemName] = useState('');
  const [quantityKg, setQuantityKg] = useState('');
  const [amountSaved, setAmountSaved] = useState('');

  const fetchData = useCallback(async () => {
    if (!user) return;
    try {
      const [statsRes, historyRes, leaderboardRes] = await Promise.all([
        api.get('/budget-hero/stats'),
        api.get('/budget-hero/history'),
        api.get('/budget-hero/leaderboard')
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data.purchases);
      setLeaderboard(leaderboardRes.data.leaderboard);
    } catch (error) {
      console.error('Error fetching budget hero data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!itemName || !quantityKg || !amountSaved) {
      toast.error('Remplissez tous les champs');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/budget-hero/purchase', {
        item_name: itemName,
        quantity_kg: parseFloat(quantityKg),
        amount_saved: parseFloat(amountSaved)
      });
      toast.success('Achat enregistré!');
      
      // Check for new badges and show celebration
      if (response.data.new_badges && response.data.new_badges.length > 0) {
        response.data.new_badges.forEach((badge, index) => {
          setTimeout(() => {
            toast.success(
              <div className="flex items-center gap-3">
                <span className="text-2xl">{badge.icon}</span>
                <div>
                  <p className="font-bold">Nouveau badge!</p>
                  <p className="text-sm">{badge.name}</p>
                </div>
              </div>,
              { duration: 5000 }
            );
          }, index * 1500);
        });
      }
      
      setItemName('');
      setQuantityKg('');
      setAmountSaved('');
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (purchaseId) => {
    try {
      await api.delete(`/budget-hero/purchase/${purchaseId}`);
      toast.success('Achat supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-CA', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-24" data-testid="budget-hero-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Leaf className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="font-heading text-xl uppercase tracking-tight text-foreground">
                Budget Éco
              </h1>
              <p className="text-sm text-muted-foreground">Anti-gaspillage alimentaire</p>
            </div>
          </div>
          <a
            href="https://www.foodhero.com/fr"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            data-testid="open-foodhero-btn"
          >
            <ExternalLink className="w-4 h-4" />
            Food Hero
          </a>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          {/* KG Saved */}
          <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-2xl p-4 text-center">
            <Package className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
            <p className="text-3xl font-black text-emerald-400" data-testid="total-kg">
              {stats?.total_kg_saved?.toFixed(1) || 0}
            </p>
            <p className="text-xs text-emerald-300/70 uppercase tracking-wide">kg sauvés</p>
          </div>

          {/* Dollars Saved */}
          <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-2xl p-4 text-center">
            <DollarSign className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <p className="text-3xl font-black text-amber-400" data-testid="total-dollars">
              {stats?.total_dollars_saved?.toFixed(0) || 0}$
            </p>
            <p className="text-xs text-amber-300/70 uppercase tracking-wide">économisés</p>
          </div>

          {/* CO2 Saved */}
          <div className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 border border-teal-500/30 rounded-2xl p-4 text-center">
            <Leaf className="w-6 h-6 text-teal-400 mx-auto mb-2" />
            <p className="text-3xl font-black text-teal-400" data-testid="total-co2">
              {stats?.total_co2_saved?.toFixed(1) || 0}
            </p>
            <p className="text-xs text-teal-300/70 uppercase tracking-wide">kg CO2 évités</p>
          </div>
        </div>

        {/* CO2 Equivalence */}
        {stats?.total_co2_saved > 0 && (
          <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-4 flex items-center gap-3">
            <Car className="w-8 h-8 text-emerald-400" />
            <div>
              <p className="text-sm text-emerald-300">
                Équivalent à <span className="font-bold">{Math.round(stats.total_co2_saved / 0.21)} km</span> de trajet en voiture évités
              </p>
              <p className="text-xs text-muted-foreground">
                Basé sur 210g CO2/km en moyenne
              </p>
            </div>
          </div>
        )}

        {/* Add Purchase Button */}
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 rounded-xl"
            data-testid="add-purchase-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Ajouter un achat Food Hero
          </Button>
        )}

        {/* Add Purchase Form */}
        {showForm && (
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="purchase-form">
            <h3 className="font-heading text-sm uppercase tracking-wide text-primary mb-4 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" />
              Nouvel achat
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Article acheté</label>
                <Input
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  placeholder="Ex: Poulet, légumes, yogourt..."
                  className="bg-background"
                  data-testid="item-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Quantité (kg)</label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={quantityKg}
                    onChange={(e) => setQuantityKg(e.target.value)}
                    placeholder="1.5"
                    className="bg-background"
                    data-testid="quantity-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Économie ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amountSaved}
                    onChange={(e) => setAmountSaved(e.target.value)}
                    placeholder="5.00"
                    className="bg-background"
                    data-testid="amount-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  data-testid="submit-purchase-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Enregistrer'
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="bg-primary/5 border-b border-border px-5 py-3 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-400" />
              <h3 className="font-heading text-sm uppercase tracking-wide text-primary">
                Classement Anti-Gaspi
              </h3>
            </div>
            <div className="divide-y divide-border">
              {leaderboard.slice(0, 5).map((entry) => (
                <div 
                  key={entry.user_id}
                  className={`px-5 py-3 flex items-center justify-between ${
                    entry.is_current_user ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      entry.rank === 1 ? 'bg-amber-500 text-white' :
                      entry.rank === 2 ? 'bg-gray-400 text-white' :
                      entry.rank === 3 ? 'bg-amber-700 text-white' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {entry.rank}
                    </span>
                    <span className={`text-sm ${entry.is_current_user ? 'font-bold text-primary' : 'text-foreground'}`}>
                      {entry.name}
                      {entry.is_current_user && ' (Vous)'}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-emerald-400">{entry.kg_saved} kg</p>
                    <p className="text-xs text-muted-foreground">{entry.co2_saved} kg CO2</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Purchase History */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="bg-primary/5 border-b border-border px-5 py-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-sm uppercase tracking-wide text-primary">
              Historique
            </h3>
          </div>
          {history.length > 0 ? (
            <div className="divide-y divide-border">
              {history.map((purchase) => (
                <div key={purchase.purchase_id} className="px-5 py-4 flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{purchase.item_name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-emerald-400">{purchase.quantity_kg} kg</span>
                      <span className="text-xs text-amber-400">{purchase.amount_saved}$ économisés</span>
                      <span className="text-xs text-muted-foreground">{formatDate(purchase.date)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(purchase.purchase_id)}
                    className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                    data-testid={`delete-${purchase.purchase_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <ShoppingBag className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                Aucun achat enregistré
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Utilisez Food Hero et enregistrez vos économies ici!
              </p>
            </div>
          )}
        </div>

        {/* Food Hero CTA */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-center">
          <h3 className="font-heading text-lg text-white mb-2">
            Découvrez Food Hero
          </h3>
          <p className="text-sm text-emerald-100 mb-4">
            Achetez des produits en surplus à 25-60% de rabais dans les épiceries du Québec
          </p>
          <a
            href="https://www.foodhero.com/fr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-emerald-600 px-6 py-3 rounded-xl font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Télécharger l'app
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default BudgetHero;
